import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { authenticateWithToken, handlePassportAuthTokens } from '../lib/auth';
import { verifyMagicLinkTokenSSO } from '../lib/api/public';
import { useEventPublicInfo } from './api/public/use-event-public-details';
import { IAuthContext, useAuth } from '../lib/context-providers/auth-context';
import { DEFAULT_ERROR_MESSAGE, ML_ACCESS_TOKEN } from '../lib/constants';
import { PassportAuthTokens } from '../lib/api';
import useDefaultRoutePage from './use-default-route-page';

type MagicLinkAuthResult = {
  authenticate: (magicLink: string) => Promise<void>;
  isAuthenticating: boolean;
  error: string | null;
};

/**
 * Hook that provides a function to handle in-memory authentication when a
 * magicLink URL is received from the registration subscription, avoiding a
 * full page reload.
 *
 * After successful auth, navigates to the destination path encoded in the magic
 * link URL (e.g. /stream/150406/106381?playeronly=true) so that query params
 * like playeronly are preserved without a full page reload.
 *
 * Returns an `authenticate` function so the caller has full control over when
 * to start the auth process.
 */
export const useMagicLinkAuth = (): MagicLinkAuthResult => {
  const auth = useAuth() as IAuthContext;
  const { data } = useEventPublicInfo();
  const history = useHistory();
  const defaultRoutePage = useDefaultRoutePage();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasAuthStarted = useRef(false);

  const authenticate = async (magicLink: string) => {
    if (hasAuthStarted.current) return;
    hasAuthStarted.current = true;

    try {
      const magicLinkUrl = new URL(magicLink);
      const mlAccessToken = magicLinkUrl.searchParams.get(ML_ACCESS_TOKEN);

      if (!mlAccessToken) {
        setError('Invalid magic link format.');
        return;
      }

      setIsAuthenticating(true);

      const isSSOEnabled = data?.attendeeSsoEnabled;

      if (isSSOEnabled) {
        const response = await verifyMagicLinkTokenSSO(mlAccessToken);
        if (response.fusionAuthIdpRedirectUrl) {
          window.location.href = response.fusionAuthIdpRedirectUrl;
          return;
        } else if (response?.accessToken) {
          handlePassportAuthTokens(response as PassportAuthTokens);
          auth.setAsAuthenticated();
        }
      } else {
        await authenticateWithToken(mlAccessToken);
        auth.setAsAuthenticated();
      }

      // Navigate to the destination path from the magic link URL,
      const destinationPath = magicLinkUrl.pathname;
      magicLinkUrl.searchParams.delete(ML_ACCESS_TOKEN);
      const remainingSearch = magicLinkUrl.searchParams.toString();

      if (destinationPath && destinationPath !== '/' && destinationPath !== window.location.pathname) {
        history.replace({
          pathname: destinationPath,
          search: remainingSearch ? `?${remainingSearch}` : '',
        });
      } else if (destinationPath === '/' && defaultRoutePage !== window.location.pathname) {
        history.replace(defaultRoutePage);
      }
    } catch {
      setError(DEFAULT_ERROR_MESSAGE);
      auth.setAsNotAuthenticated();
    } finally {
      setIsAuthenticating(false);
    }
  };

  return { authenticate, isAuthenticating, error };
};
